/**
 * Un lote de activación y **sus rechazos auditados**.
 *
 * Es la traza que pide un auditor: qué se intentó activar, contra qué lote, y
 * en qué estado estaba realmente la unidad. Sale de `activation_conflict`, que
 * el endpoint escribe en la misma operación que la activación.
 *
 * Acá no se renderiza el `unit_id` aunque el endpoint lo devuelva: la fila se
 * identifica por su número de caso. El `unit_id` es un id interno y ofuscarlo
 * es media razón de ser del formato del código (regla 3 del dominio); pintarlo
 * en una tabla del dashboard lo publicaría igual.
 */
import type { ReactNode } from 'react';
import { Link, useParams } from 'react-router-dom';

import { useApiQuery, useKeysetQuery } from '@/api/hooks';
import { activationKeys } from '@/api/keys';
import type { components } from '@/api/schema';
import { useI18n } from '@/i18n/useI18n';
import { Badge } from '@/ui/Badge';
import { Button } from '@/ui/Button';
import { Card, CardBody, CardHeader } from '@/ui/Card';
import { EmptyState, ErrorState, LoadingState, QueryBoundary } from '@/ui/states';
import { Table, Td, Th } from '@/ui/Table';
import { OutcomeBreakdown } from './ActivationResult';
import { outcomeLabelKey, outcomeTone, unitStatusKey } from './outcomes';

type ActivationBatch = components['schemas']['ActivationBatchOut'];
type ActivationConflict = components['schemas']['ActivationConflictOut'];

const CONFLICTS_PAGE = 50;

export function ActivationDetailPage(): ReactNode {
  const { t, fmt } = useI18n();
  const params = useParams<{ activationId: string }>();
  const activationId = Number.parseInt(params.activationId ?? '', 10);
  const valid = Number.isFinite(activationId);

  const batch = useApiQuery<ActivationBatch>(
    activationKeys.detail(activationId),
    `/api/v1/activations/${activationId}`,
    undefined,
    { enabled: valid },
  );
  const conflicts = useKeysetQuery<ActivationConflict>(
    activationKeys.conflicts(activationId, CONFLICTS_PAGE),
    `/api/v1/activations/${activationId}/conflicts`,
    undefined,
    { limit: CONFLICTS_PAGE, enabled: valid },
  );

  if (!valid) {
    return <EmptyState title={t('activation.detail.notFound')} />;
  }

  return (
    <div className="mx-auto max-w-5xl space-y-4">
      <Link to="/activation" className="text-sm text-accent underline underline-offset-2">
        {t('activation.detail.back')}
      </Link>

      <Card>
        <QueryBoundary query={batch} loadingRows={5}>
          {(data) => (
            <>
              <CardHeader
                title={<span className="font-mono">{data.client_batch_id}</span>}
                description={t('activation.detail.description', {
                  batch: data.batch_id,
                  line: data.line_id ?? '—',
                })}
                actions={
                  data.rejected > 0 ? (
                    <Badge tone="critical">
                      {t('activation.detail.rejected', { count: fmt.number(data.rejected) })}
                    </Badge>
                  ) : (
                    <Badge tone="verified">{t('activation.detail.clean')}</Badge>
                  )
                }
              />
              <CardBody className="space-y-4">
                <dl className="grid gap-x-6 gap-y-3 sm:grid-cols-3">
                  <Field label={t('activation.runs.column.received')} value={fmt.dateTime(data.received_at)} />
                  <Field
                    label={t('activation.detail.clientSentAt')}
                    value={data.client_sent_at === null || data.client_sent_at === undefined
                      ? '—'
                      : fmt.dateTime(data.client_sent_at)}
                  />
                  <Field
                    label={t('activation.detail.duration')}
                    value={data.duration_ms === null || data.duration_ms === undefined
                      ? '—'
                      : t('activation.detail.durationValue', { ms: fmt.number(data.duration_ms) })}
                  />
                  <Field label={t('activation.runs.column.requested')} value={fmt.number(data.requested)} />
                  <Field label={t('activation.result.activated')} value={fmt.number(data.activated)} />
                  <Field label={t('activation.result.already')} value={fmt.number(data.already)} />
                </dl>
                <OutcomeBreakdown outcomes={data.outcomes} />
              </CardBody>
            </>
          )}
        </QueryBoundary>
      </Card>

      <Card>
        <CardHeader
          title={t('activation.conflicts.title')}
          description={t('activation.conflicts.description')}
        />

        {conflicts.query.isPending && <LoadingState rows={4} />}

        {conflicts.query.isError && (
          <CardBody>
            <ErrorState
              error={conflicts.query.error}
              onRetry={() => { void conflicts.query.refetch(); }}
            />
          </CardBody>
        )}

        {!conflicts.query.isPending && !conflicts.query.isError && conflicts.items.length === 0 && (
          <EmptyState
            title={t('activation.conflicts.empty')}
            detail={t('activation.conflicts.emptyHint')}
          />
        )}

        {conflicts.items.length > 0 && (
          <>
            <Table caption={t('activation.conflicts.description')}>
              <thead>
                <tr>
                  <Th>{t('activation.conflicts.column.case')}</Th>
                  <Th>{t('activation.conflicts.column.reason')}</Th>
                  <Th>{t('activation.conflicts.column.attempted')}</Th>
                  <Th>{t('activation.conflicts.column.existing')}</Th>
                  <Th>{t('activation.conflicts.column.status')}</Th>
                  <Th>{t('activation.conflicts.column.when')}</Th>
                </tr>
              </thead>
              <tbody>
                {conflicts.items.map((conflict) => {
                  const reason = outcomeLabelKey(conflict.reason);
                  const status = unitStatusKey(conflict.existing_status);
                  return (
                    <tr key={conflict.id}>
                      {/* Identificador del caso, nunca el `unit_id`. */}
                      <Td className="font-mono text-ink-muted">#{conflict.id}</Td>
                      <Td>
                        <Badge tone={outcomeTone(conflict.reason)}>
                          {reason === null ? conflict.reason : t(reason)}
                        </Badge>
                      </Td>
                      <Td className="text-ink-muted">
                        {conflict.attempted_batch_id === null ||
                        conflict.attempted_batch_id === undefined
                          ? '—'
                          : `#${conflict.attempted_batch_id}`}
                      </Td>
                      <Td className="text-ink-muted">
                        {conflict.existing_batch_id === null ||
                        conflict.existing_batch_id === undefined
                          ? '—'
                          : `#${conflict.existing_batch_id}`}
                      </Td>
                      <Td className="text-ink-muted">
                        {status === null ? (conflict.existing_status ?? '—') : t(status)}
                      </Td>
                      <Td className="text-ink-muted">{fmt.dateTime(conflict.created_at)}</Td>
                    </tr>
                  );
                })}
              </tbody>
            </Table>

            <CardBody className="flex items-center justify-center border-t border-border">
              {conflicts.hasMore ? (
                <Button size="sm" onClick={conflicts.loadMore} disabled={conflicts.isLoadingMore}>
                  {conflicts.isLoadingMore ? t('state.loadingMore') : t('state.loadMore')}
                </Button>
              ) : (
                <p className="text-xs text-ink-subtle">{t('state.allLoaded')}</p>
              )}
            </CardBody>
          </>
        )}
      </Card>
    </div>
  );
}

function Field({ label, value }: { label: string; value: ReactNode }): ReactNode {
  return (
    <div>
      <dt className="text-xs tracking-wide text-ink-subtle uppercase">{label}</dt>
      <dd className="mt-0.5 text-sm text-ink">{value}</dd>
    </div>
  );
}
